import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { observer } from "mobx-react";
import { Spinner, Alert, Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { formatTimestamp, formatMultipleItemTitle } from "../lib/utils";
import { DEV } from "../lib/configMgr";
import { oneshot } from "../queries/autosuggest";
import { rootStore } from "../stores";
import Analytics from "../lib/Analytics";
import MemoryProfileDrawer from "./MemoryProfileDrawer";
import ActiveAgentIcon from "./ActiveAgentIcon";
import { useRunningConversations } from "../llm/runningConversations";
import "../styles/MemoryProfileDrawer.css";

/**
 * HistoryDrawer - Cloud-backed history drawer for conversations and searches
 *
 * Manages history via HistoryStore which uses the REST API (DynamoDB + S3 backend).
 * Supports pagination, error handling, and edit/regenerate/duplicate functionality.
 *
 * Key features:
 * - Primary storage: Cloud REST API via HistoryStore
 * - Pagination for large history lists
 * - Online/offline detection with graceful degradation
 * - Edit, regenerate, and duplicate buttons
 * - Loading and error states
 * - Auto-refresh on mount
 */

const PRESET_COLORS = [
  '#e57373', '#f4a261', '#ffd166', '#90be6d', '#4cc9f0',
  '#4361ee', '#9b5de5', '#2ec4b6',
];

function getContrastColor(hexColor) {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

const HistoryDrawer = observer(({
  mode = "conversation", // "conversation" or "search"
  currentItemId = null, // UUID of currently active item (initialized from localStorage on mount)
  isSessionActive = false, // True when LLM streaming or search in progress
  onLoadItem,
  onLoadStart = null, // Called before fetching item content
  onLoadEnd = null,   // Called after fetching item content (success or failure)
  onNewItem,
  onClearCurrent, // Called when deleting the current item
  userStore, // Required for authentication
  newItemLabel = "New",
  emptyMessage = "No saved items",
  emptyHint = "Start using the app to create your first saved item",
  className = "",
}) => {

  const [isOpen, setIsOpen] = useLocalStorage('history-drawer-open', true);
  // Drawer tab: 'conversations' (default) or 'memory'. The Memory icon
  // swaps the drawer body in place rather than popping out a second
  // panel — keeps everything in one spatial neighborhood. Persisted so
  // the user lands back where they were on reload.
  const [drawerTab, setDrawerTab] = useLocalStorage('history-drawer-tab', 'conversations');
  const memoryTabActive = drawerTab === 'memory' && !!userStore?.isAdmin;
  // PLEX-6253: the archived conversations, with Restore. Not remembered across a reload, so the
  // drawer never reopens on it with the conversations out of sight.
  const [archivedView, setArchivedView] = useState(false);
  const archivedTabActive = archivedView && mode === 'conversation' && !memoryTabActive;

  // User-resizable width. Min keeps the header icon row from clipping;
  // max keeps the chat area usable on smaller laptops.
  const DRAWER_WIDTH_MIN = 280;
  const DRAWER_WIDTH_MAX = 600;
  const [drawerWidth, setDrawerWidth] = useLocalStorage('history-drawer-width', 360);
  const [resizing, setResizing] = useState(false);

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = drawerWidth;
    setResizing(true);
    const onMove = (mv) => {
      const next = Math.min(DRAWER_WIDTH_MAX, Math.max(DRAWER_WIDTH_MIN, startWidth + (mv.clientX - startX)));
      setDrawerWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      setResizing(false);
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const [hoverMenu, setHoverMenu] = useState({ id: null, type: null, top: 0, right: 0, expanded: false });
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [restoringId, setRestoringId] = useState(null);
  const [loadingItem, setLoadingItem] = useState(null);
  const [pendingItemId, setPendingItemId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [regeneratingIds, setRegeneratingIds] = useState(new Set());
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [navConfirmItem, setNavConfirmItem] = useState(null);

  // Project state
  const [expandedProjects, setExpandedProjects] = useLocalStorage('history-projects-expanded', {});
  const [creatingProject, setCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editProjectTitle, setEditProjectTitle] = useState('');
  const [deleteProjectDialog, setDeleteProjectDialog] = useState({ open: false, project: null });
  const [dragState, setDragState] = useState({ draggingItemId: null, draggingItemProjectId: null, dragOverProjectId: null });
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  // Anchor item ID for shift-click range selection (set on cmd-click or plain selection)
  const [selectionAnchorId, setSelectionAnchorId] = useState(null);
  // ID of project whose color picker popup is open, and fixed position of its button
  const [colorPicker, setColorPicker] = useState({ projectId: null, top: 0, left: 0 });
  const [filterText, setFilterText] = useState('');

  // Get history store from rootStore (already initialized with userStore)
  const { historyStore } = rootStore;

  // Collapse all projects/folders when switching between search and chat modes
  useEffect(() => {
    setExpandedProjects({});
  }, [mode]);

  // Load history from cloud on mount
  useEffect(() => {
    if (historyStore && userStore?.isAuthenticated && historyStore.isOnline) {
      historyStore.loadHistory(mode.toUpperCase());
    }
  }, [mode, userStore?.isAuthenticated, historyStore]);

  // PLEX-6361: conversations with an agent still working get an icon next to their title.
  const reloadConversations = useCallback(() => {
    historyStore.loadHistory('CONVERSATION').catch(() => {});
  }, [historyStore]);
  const runningIds = useRunningConversations(mode === "conversation" ? historyStore.conversations : null,
                                             { auth: userStore?.authData, onEnded: reloadConversations });

  // Refresh on tab focus — single fetch for the current mode only
  useEffect(() => {
    const handleVisibility = () => historyStore.refreshOnFocus(mode.toUpperCase());
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [mode, historyStore]);

  // When the active item is inside a folder, expand that folder so it's visible
  useEffect(() => {
    if (!currentItemId || !historyStore.sortedProjects.length) return;
    const allItems = [...(historyStore.conversations || []), ...(historyStore.searches || [])];
    const activeItem = allItems.find(i => i.id === currentItemId);
    if (activeItem?.context?.folderId) {
      setExpandedProjects(prev => ({ ...prev, [activeItem.context.folderId]: true }));
    }
  }, [currentItemId, historyStore.sortedProjects.length]);

  // Handle ESC key to close drawer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleLoadItem = async (item) => {
    if (!onLoadItem) return;

    setLoadingItem(item.id);
    setPendingItemId(item.id);
    onLoadStart?.();

    try {
      // Fetch full item data from cloud
      let fullItem;
      if (mode === 'conversation') {
        // Conversations need full data with turns; null means not found — fall back to list item
        fullItem = await historyStore.getConversation(item.id) || item;
      } else {
        // For searches, we only need the URL which is already in item.context
        // No need to fetch from API - just use the item we have
        fullItem = item;
      }
      Analytics.event(Analytics.EVENTS.HISTORY_ITEM_LOAD, {
        item_id: item.id,
        item_type: item.type || mode || "unknown",
        title: (item.context?.title || "").slice(0, 100),
      });
      onLoadItem(fullItem);
    } catch (error) {
      // Item appears in list but can't be fetched (likely corrupted/deleted)
      if (error.status === 404) {
        console.warn(`Item ${item.id} not found (appears in list but deleted/corrupted)`);
      } else {
        console.error('Failed to load item:', error);
      }
      // Fallback to item metadata if full load fails
      onLoadItem(item);
    } finally {
      setLoadingItem(null);
      setPendingItemId(null);
      onLoadEnd?.();
    }
  };

  const handleDeleteItem = async (item, e) => {
    e.stopPropagation();

    // Check if this is the currently active item
    const isDeletingCurrent = isCurrentItem(item);

    // Add to deleting set to trigger CSS animation
    setDeletingIds(prev => new Set([...prev, item.id]));

    try {
      // Use mode to determine type since we don't have type field anymore
      const itemType = mode === 'conversation' ? 'CONVERSATION' : 'SEARCH';
      await historyStore.deleteItem(item.id, itemType);

      // If we deleted the current item, clear it (don't start a new one)
      if (isDeletingCurrent && onClearCurrent) {
        onClearCurrent();
      }

      // Wait for animation to complete before removing from deleting set
      setTimeout(() => {
        setDeletingIds(prev => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }, 300); // Match CSS animation duration
    } catch (error) {
      console.error('Failed to delete item:', error);
      // Remove from deleting set on error
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  // PLEX-6253: archive hides a conversation from the lists until it's restored. It stays
  // openable, so the open conversation isn't cleared.
  const handleArchiveItem = async (item, e) => {
    e.stopPropagation();
    try {
      await historyStore.archiveConversation(item.id);
    } catch (error) {
      console.error('Failed to archive conversation:', error);
      toast.error(`Failed to archive: ${error.message || 'Unknown error'}`);
    }
  };

  const handleRestoreItem = async (item, e) => {
    e.stopPropagation();
    try {
      await historyStore.unarchiveConversation(item.id);
    } catch (error) {
      console.error('Failed to restore conversation:', error);
      toast.error(`Failed to restore: ${error.message || 'Unknown error'}`);
    }
  };

  useEffect(() => {
    if (archivedTabActive && userStore?.isAuthenticated) historyStore.loadArchived();
  }, [archivedTabActive, historyStore, userStore?.isAuthenticated]);

  const handleNewItem = (e) => {
    if (e?.ctrlKey || e?.metaKey || e?.shiftKey) {
      Analytics.event(Analytics.EVENTS.HISTORY_NEW_ITEM, { mode, new_tab: true });
      const url = mode === "conversation" ? "/#llm" : "/";
      window.open(url, '_blank');
      return;
    }
    Analytics.event(Analytics.EVENTS.HISTORY_NEW_ITEM, { mode, new_tab: false });
    if (isSessionActive) {
      setNavConfirmItem({ _newItem: true });
      return;
    }
    if (onNewItem) {
      onNewItem();
    }
  };

  const handleEditItem = (item, e) => {
    e.stopPropagation();
    setEditingId(item.id);
    setEditTitle(item.context?.title || '');
  };

  const handleSaveEdit = (item, e) => {
    if (e) {
      e.stopPropagation();
    }

    if (!editTitle.trim()) {
      setEditingId(null);
      return;
    }

    Analytics.event(Analytics.EVENTS.HISTORY_RENAME, {
      item_id: item.id,
      item_type: item.type || mode || "unknown",
    });

    const newTitle = editTitle.trim();

    // Close edit mode immediately — don't block on the API call
    setEditingId(null);

    // IMPORTANT: Send full context with updated title to avoid losing other fields
    const updatedContext = {
      ...item.context,
      title: newTitle
    };

    // Optimistic update — show the new title immediately before the PUT completes.
    // _mergePreservingPending in loadHistory protects this from being overwritten
    // by a concurrent refresh that returns stale server data.
    if (mode === "conversation") {
      historyStore.patchConversationContext(item.id, updatedContext);
    } else {
      historyStore.patchSearchContext(item.id, updatedContext);
    }

    const updatePromise = mode === "conversation"
      ? historyStore.updateConversation(item.id, { context: updatedContext })
      : historyStore.updateSearch(item.id, { context: updatedContext });

    updatePromise.catch(error => {
      console.error("Failed to update item title:", error);
      toast.error(`Failed to save title: ${error.message || 'Unknown error'}`);
    });
  };

  const handleCancelEdit = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setEditingId(null);
    setEditTitle('');
  };

  const handleEditKeyDown = (item, e) => {
    if (e.key === 'Enter') {
      handleSaveEdit(item, e);
    } else if (e.key === 'Escape') {
      handleCancelEdit(e);
    }
  };

  const handleRegenerateTitle = async (item, e) => {
    e.stopPropagation();
    Analytics.event(Analytics.EVENTS.HISTORY_REGEN_TITLE, {
      item_id: item.id,
      item_type: item.type || mode || "unknown",
    });

    setRegeneratingIds(prev => new Set([...prev, item.id]));

    try {
      let newTitle;

      if (mode === "conversation") {
        // For conversations, use LLM to generate title
        if (!userStore?.authData) {
          console.warn("Cannot regenerate title: no authentication");
          setRegeneratingIds(prev => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          return;
        }

        // Fetch full conversation data
        const fullItem = await historyStore.getConversation(item.id);

        if (!fullItem?.turns || fullItem.turns.length === 0) {
          console.warn("Cannot regenerate title: no conversation turns");
          setRegeneratingIds(prev => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          return;
        }

        // Extract conversation text from first few turns
        const limitedTurns = fullItem.turns.slice(0, 3);
        const conversationText = limitedTurns
          .map(turn => {
            const textContent = turn.content
              .filter(block => block.text)
              .map(block => block.text)
              .join(' ');
            return `${turn.role}: ${textContent}`;
          })
          .join('\n');

        if (!conversationText.trim()) {
          console.warn("Cannot regenerate title: no text content");
          setRegeneratingIds(prev => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          return;
        }

        // Use oneshot to generate a new title
        const prompt = `Generate a concise, descriptive title for this conversation.
Use natural language with proper capitalization. Maximum 60 characters.
Only return the title, nothing else.

Conversation:
${conversationText}`;

        const options = {
          input_format: "text",
          output_format: "text",
        };

        const response = await oneshot(prompt, userStore.authData, options);
        const titleText = await response.text();

        // Clean up the title
        newTitle = titleText
          .trim()
          .replace(/^["']|["']$/g, '')
          .substring(0, 60);

      } else if (mode === "search") {
        // For searches: check if it has conversation turns
        // If no turns → revert to default title (query entities)
        // If has turns → regenerate from conversation (like regular conversations)

        // Fetch full search data first
        const fullItem = await historyStore.getSearch(item.id);

        // Fall back to list item if backend doesn't return context
        const searchItem = fullItem?.context ? fullItem : item;

        const hasTurns = searchItem.turns && searchItem.turns.length > 0;

        if (hasTurns) {
          // Search with conversation: use LLM to generate from conversation
          const conversationText = searchItem.turns
            .slice(0, 3)
            .map(turn => {
              const textContent = turn.content
                .filter(block => block.text)
                .map(block => block.text)
                .join(' ');
              return `${turn.role}: ${textContent}`;
            })
            .join('\n');

          if (!conversationText.trim()) {
            console.warn("Cannot regenerate title: no conversation text");
            setRegeneratingIds(prev => {
              const next = new Set(prev);
              next.delete(item.id);
              return next;
            });
            return;
          }

          const prompt = `Generate a concise, descriptive title for this conversation.
Use natural language with proper capitalization. Maximum 60 characters.
Only return the title, nothing else.

Conversation:
${conversationText}`;

          const options = {
            input_format: "text",
            output_format: "text",
          };

          const response = await oneshot(prompt, userStore.authData, options);
          const titleText = await response.text();

          newTitle = titleText
            .trim()
            .replace(/^["']|["']$/g, '')
            .substring(0, 60);
        } else {
          // Search without conversation: revert to default title from query entities
          if (!searchItem.context?.queryEntities || searchItem.context.queryEntities.length === 0) {
            console.warn("Cannot revert title: no query entities");
            setRegeneratingIds(prev => {
              const next = new Set(prev);
              next.delete(item.id);
              return next;
            });
            return;
          }

          const entityNames = searchItem.context.queryEntities.map(qe =>
            qe.name.replace(/^(query|smiles):/, "")
          );
          newTitle = formatMultipleItemTitle(entityNames, 3);
        }
      }

      if (newTitle) {
        // IMPORTANT: Send full context with updated title to avoid losing other fields
        const updatedContext = {
          ...item.context,
          title: newTitle
        };

        if (mode === "conversation") {
          historyStore.patchConversationContext(item.id, updatedContext);
          await historyStore.updateConversation(item.id, { context: updatedContext });
        } else if (mode === "search") {
          historyStore.patchSearchContext(item.id, updatedContext);
          await historyStore.updateSearch(item.id, { context: updatedContext });
        }
      }
    } catch (error) {
      console.error("Failed to regenerate title:", error);
      toast.error(`Failed to regenerate title: ${error.message || 'Unknown error'}`);
    } finally {
      setRegeneratingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleDuplicateItem = async (item, e) => {
    e.stopPropagation();
    Analytics.event(Analytics.EVENTS.HISTORY_DUPLICATE, {
      item_id: item.id,
      item_type: item.type || mode || "unknown",
    });

    try {
      // For searches, try to fetch full data, but fall back to list item if backend doesn't return context
      let fullItem;
      if (mode === "conversation") {
        fullItem = await historyStore.getConversation(item.id);
      } else {
        fullItem = await historyStore.getSearch(item.id);

        // If backend didn't return context, fall back to using the list item
        if (!fullItem?.context && item.context) {
          fullItem = item;
        }
      }

      if (!fullItem) {
        toast.error("Failed to duplicate: Item not found");
        return;
      }

      if (!fullItem.context) {
        toast.error("Failed to duplicate: Item is missing required data");
        return;
      }

      if (mode === "search" && !fullItem.context.url) {
        toast.error("Failed to duplicate: Search is missing URL");
        return;
      }

      // Helper to generate next copy number
      const getNextCopyTitle = (baseTitle) => {
        // Extract base title and existing copy number
        const copyMatch = baseTitle.match(/^(.*?)\s*\(copy(?:\s+(\d+))?\)$/);
        const baseName = copyMatch ? copyMatch[1] : baseTitle;

        // Find all existing titles with same base
        const items = mode === "conversation" ? historyStore.conversations : historyStore.searches;
        const existingCopyNumbers = items
          .map(i => {
            const title = i.context?.title || '';
            const match = title.match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\(copy(?:\\s+(\\d+))?\\)$`));
            if (match) {
              return match[1] ? parseInt(match[1]) : 1;
            }
            return 0;
          })
          .filter(n => n > 0);

        const nextNumber = existingCopyNumbers.length > 0 ? Math.max(...existingCopyNumbers) + 1 : 1;
        return nextNumber === 1 ? `${baseName} (copy)` : `${baseName} (copy ${nextNumber})`;
      };

      // For conversations, create clean duplicate with only required fields
      if (mode === "conversation") {
        const newTitle = getNextCopyTitle(fullItem.context.title || 'Untitled');

        // Only send turns and context (no id, type, timestamp, metadata)
        const duplicateData = {
          turns: fullItem.turns,
          context: {
            ...fullItem.context,
            id: undefined, // Force server to generate new UUID
            title: newTitle,
          }
        };
        await historyStore.saveConversation(duplicateData);
      } else {
        // For searches, duplicate with same URL (same search results)
        // but different title and potentially different conversation

        // Ensure URL is valid
        let baseUrl = fullItem.context.url;
        if (!baseUrl || baseUrl === 'undefined') {
          toast.error("Failed to duplicate: Search has invalid URL");
          return;
        }

        const newTitle = getNextCopyTitle(fullItem.context.title || 'Search');

        const duplicateData = {
          // Don't include id - backend will generate UUID for this new history item
          context: {
            ...fullItem.context,
            title: newTitle,
          },
          // Include turns if they exist
          turns: fullItem.turns || []
        };

        await historyStore.saveSearch(duplicateData);
      }
    } catch (error) {
      console.error("Failed to duplicate item:", error);
      toast.error(`Failed to duplicate: ${error.message || 'Unknown error'}`);
    }
  };

  const handleLoadMore = () => {
    if (historyStore.hasMore && !historyStore.loading) {
      historyStore.loadMore(mode.toUpperCase());
    }
  };

  const handleRetry = () => {
    historyStore.clearError();
    historyStore.loadHistory(mode.toUpperCase());
  };

  const getItemUrl = (item) => {
    if (mode === 'conversation') {
      return `/#llm&history_id=${item.id}`;
    }
    if (item.context?.url) {
      const url = new URL(item.context.url, window.location.origin);
      const hashParams = new URLSearchParams(url.hash ? url.hash.slice(1) : '');
      hashParams.delete('history_id');
      hashParams.set('history_id', item.id);
      return `${url.pathname}${url.search}#${hashParams.toString()}`;
    }
    return '/';
  };

  const handleOpenInNewTab = (item) => {
    if (!item?._newItem) {
      Analytics.event(Analytics.EVENTS.HISTORY_OPEN_NEW_TAB, {
        item_id: item.id,
        item_type: item.type || mode || "unknown",
      });
    }
    if (item?._newItem) {
      window.open(mode === "conversation" ? "/#llm" : "/", '_blank');
    } else {
      window.open(getItemUrl(item), '_blank');
    }
    setNavConfirmItem(null);
  };

  const handleSwitchHere = (item) => {
    setNavConfirmItem(null);
    if (item?._newItem) {
      onNewItem?.();
    } else {
      handleLoadItem(item);
    }
  };

  const handleClearHistory = () => {
    setShowClearConfirm(true);
  };

  const handleConfirmClear = async () => {
    setShowClearConfirm(false);
    Analytics.event(Analytics.EVENTS.HISTORY_CLEAR, { item_type: mode.toUpperCase() });

    try {
      // clearHistoryByType() already clears local state, no need to reload
      await historyStore.clearHistoryByType(mode);

      // Clear the current active conversation/search
      if (onNewItem) {
        onNewItem();
      }
    } catch (error) {
      console.error('Failed to clear history:', error);
      toast.error(`Failed to clear history: ${error.message || 'Unknown error'}`);
    }
  };

  const handleCancelClear = () => {
    setShowClearConfirm(false);
  };

  const toggleDrawer = () => {
    const newOpen = !isOpen;
    Analytics.event(Analytics.EVENTS.HISTORY_DRAWER_TOGGLE, { open: newOpen, mode });
    setIsOpen(newOpen);
  };

  // Check if an item is currently active (or pending selection)
  const isCurrentItem = (item) => {
    if (pendingItemId) return item.id === pendingItemId;
    if (!currentItemId) return false;
    return item.id === currentItemId;
  };


  // Project toggle helpers
  const toggleProject = (projectId) => {
    const newOpen = !expandedProjects[projectId];
    Analytics.event(Analytics.EVENTS.HISTORY_PROJECT_TOGGLE, { project_id: projectId, open: newOpen });
    setExpandedProjects(prev => ({ ...prev, [projectId]: newOpen }));
  };

  const isProjectExpanded = (projectId) => expandedProjects[projectId] === true; // default collapsed

  const handleMoveItem = async (item, projectId) => {
    const itemType = item.type || (mode === 'conversation' ? 'CONVERSATION' : 'SEARCH');
    Analytics.event(Analytics.EVENTS.HISTORY_MOVE_ITEM, {
      item_id: item.id,
      item_type: itemType,
      project_id: projectId || null,
    });
    const updateData = { context: { ...item.context } };
    if (projectId) {
      updateData.context.folderId = projectId;
    } else {
      delete updateData.context.folderId;
    }

    // Optimistically update local state immediately
    if (itemType === 'CONVERSATION') {
      historyStore.patchConversationContext(item.id, updateData.context);
    } else {
      historyStore.patchSearchContext(item.id, updateData.context);
    }

    // Expand/collapse destination folder for the active item
    if (isCurrentItem(item)) {
      if (projectId) {
        setExpandedProjects(prev => ({ ...prev, [projectId]: true }));
      } else {
        setExpandedProjects({});
      }
    }

    try {
      if (itemType === 'CONVERSATION') {
        await historyStore.updateConversation(item.id, updateData);
      } else {
        await historyStore.updateSearch(item.id, updateData);
      }
    } catch (err) {
      console.error('Failed to move item:', err);
      toast.error('Failed to move item. Please try again.');
      // Roll back optimistic update
      if (itemType === 'CONVERSATION') {
        historyStore.patchConversationContext(item.id, item.context);
      } else {
        historyStore.patchSearchContext(item.id, item.context);
      }
    }
  };

  const handleDropOnProject = async (itemId, projectId) => {
    if (!itemId) return;
    Analytics.event(Analytics.EVENTS.HISTORY_DRAG_DROP, { item_id: itemId, project_id: projectId || null });
    const allItems = [
      ...(historyStore.conversations || []),
      ...(historyStore.searches || []),
    ];
    const item = allItems.find(i => i.id === itemId);
    if (item) await handleMoveItem(item, projectId);
  };

  const handleBulkMove = async (projectId) => {
    setBulkMoveOpen(false);
    const ids = [...historyStore.selectedIds];
    Analytics.event(Analytics.EVENTS.HISTORY_BULK_MOVE, { count: ids.length, project_id: projectId || null });
    const allItems = [...(historyStore.conversations || []), ...(historyStore.searches || [])];
    await Promise.all(ids.map(id => {
      const item = allItems.find(i => i.id === id);
      if (item) return handleMoveItem(item, projectId);
    }));
    historyStore.setSelectedIds(new Set());
    setSelectionAnchorId(null);
  };

  const handleBulkDelete = async () => {
    setBulkDeleteConfirm(false);
    const ids = [...historyStore.selectedIds];
    const itemType = mode === 'conversation' ? 'CONVERSATION' : 'SEARCH';
    Analytics.event(Analytics.EVENTS.HISTORY_BULK_DELETE, { count: ids.length, item_type: itemType });
    await Promise.all(ids.map(id => historyStore.deleteItem(id, itemType)));
    historyStore.setSelectedIds(new Set());
    setSelectionAnchorId(null);
  };

  const handleDeleteProjectOnly = async () => {
    const { project } = deleteProjectDialog;
    setDeleteProjectDialog({ open: false, project: null });
    Analytics.event(Analytics.EVENTS.HISTORY_PROJECT_DELETE, { project_id: project.id, with_items: false });
    // Clear folderId from all contained items first
    const allItems = [...(historyStore.conversations || []), ...(historyStore.searches || [])];
    const containedItems = allItems.filter(i => i.context?.folderId === project.id);
    await Promise.all(containedItems.map(item => handleMoveItem(item, null)));
    await historyStore.deleteProject(project.id);
  };

  const handleDeleteProjectAndItems = async () => {
    const { project } = deleteProjectDialog;
    setDeleteProjectDialog({ open: false, project: null });
    Analytics.event(Analytics.EVENTS.HISTORY_PROJECT_DELETE, { project_id: project.id, with_items: true });
    // Delete all items inside the project
    const conversationIds = new Set((historyStore.conversations || []).map(i => i.id));
    const allItems = [...(historyStore.conversations || []), ...(historyStore.searches || [])];
    const containedItems = allItems.filter(i => i.context?.folderId === project.id);
    await Promise.all(containedItems.map(item => {
      const itemType = conversationIds.has(item.id) ? 'CONVERSATION' : 'SEARCH';
      return historyStore.deleteItem(item.id, itemType);
    }));
    await historyStore.deleteProject(project.id);
  };

  // Items for the current mode. Conversations keep the backend's order: running, then unread,
  // then newest (PLEX-6253). Searches sort by title.
  const allItems = mode === "conversation"
    ? historyStore.conversations
    : historyStore.sortedSearches;

  const filterLower = filterText.trim().toLowerCase();
  const items = filterLower
    ? allItems.filter(item => (item.context?.title || '').toLowerCase().includes(filterLower))
    : allItems;

  // Items not assigned to any project
  const ungroupedItems = items.filter(item => !item.context?.folderId);

  // Derive selectedCount by iterating the Set so MobX tracks it reactively
  const selectedCount = historyStore.selectedIds ? [...historyStore.selectedIds].length : 0;

// Click-outside dismiss for color picker popup
  useEffect(() => {
    if (!colorPicker.projectId) return;
    const handler = () => setColorPicker(p => ({ ...p, projectId: null }));
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [colorPicker.projectId]);

  // Delete key handler for bulk delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && historyStore.selectedIds.size > 0) {
        e.preventDefault();
        setBulkDeleteConfirm(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [historyStore.selectedIds.size]);

  const renderItem = (item, insideProject) => (
    <div
      key={item.id}
      draggable
      onDragStart={(e) => {
        // Delay state update so browser captures drag ghost before collapse animation
        setTimeout(() => setDragState(s => ({ ...s, draggingItemId: item.id, draggingItemProjectId: item.context?.folderId || null })), 0);
      }}
      onDragEnd={() => {
        setRestoringId(item.id);
        setDragState({ draggingItemId: null, draggingItemProjectId: null, dragOverProjectId: null });
      }}
      className={`history-item${isCurrentItem(item) ? ' active' : ''}${deletingIds.has(item.id) ? ' deleting' : ''}${loadingItem === item.id ? ' loading' : ''}${historyStore.selectedIds && historyStore.selectedIds.has(item.id) ? ' selected' : ''}${(dragState.draggingItemId === item.id || restoringId === item.id) ? ' dragging' : ''}`}
      onMouseEnter={() => setHoverMenu(m => ({ ...m, id: item.id, type: 'item' }))}
      onMouseLeave={() => setHoverMenu(m => m.id === item.id ? { id: null, type: null, top: 0, right: 0, expanded: false } : m)}
      onClick={(e) => {
        // Cmd/Ctrl+click: open in new tab (browser convention)
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          window.open(getItemUrl(item), '_blank');
          return;
        }
        // Shift+click: range selection from anchor to this item
        if (e.shiftKey && selectionAnchorId) {
          e.preventDefault();
          const allVisible = [
            ...historyStore.sortedProjects.flatMap(p => items.filter(i => i.context?.folderId === p.id)),
            ...ungroupedItems,
          ];
          const anchorIdx = allVisible.findIndex(i => i.id === selectionAnchorId);
          const thisIdx = allVisible.findIndex(i => i.id === item.id);
          if (anchorIdx !== -1 && thisIdx !== -1) {
            const [from, to] = anchorIdx < thisIdx ? [anchorIdx, thisIdx] : [thisIdx, anchorIdx];
            const next = new Set(allVisible.slice(from, to + 1).map(i => i.id));
            historyStore.setSelectedIds(next);
            return;
          }
        }
        // Plain click: clear selection, load item
        if (historyStore.selectedIds.size > 0) {
          historyStore.setSelectedIds(new Set());
          setSelectionAnchorId(null);
        }
        if (isSessionActive && !isCurrentItem(item)) {
          setNavConfirmItem(item);
        } else {
          handleLoadItem(item);
        }
      }}
      onTransitionEnd={(e) => {
        if (e.target !== e.currentTarget) return;
        if (deletingIds.has(item.id)) {
          setDeletingIds(prev => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
        }
        if (restoringId === item.id && e.propertyName === 'max-height') {
          setRestoringId(null);
        }
      }}
      title={(() => {
        const parts = [
          new Date(item.modified || item.created).toLocaleString()
        ];
        if (mode === "conversation" && item.metadata?.turnCount) {
          parts.push(`${item.metadata.turnCount} ${item.metadata.turnCount === 1 ? 'turn' : 'turns'}`);
        } else if (mode === "search") {
          if (item.context?.queryEntities?.length) {
            parts.push(`${item.context.queryEntities.length} ${item.context.queryEntities.length === 1 ? 'query' : 'queries'}`);
          }
          if (item.context?.totalResults > 0) {
            parts.push(`${item.context.totalResults.toLocaleString()} results`);
          }
        }
        if (DEV && item.id) {
          parts.push(`ID: ${item.id}`);
        }
        return parts.join(' • ');
      })()}
    >
      <span className="drag-handle" onMouseDown={e => e.stopPropagation()} />
      <span
        className={`item-checkbox${historyStore.selectedIds.has(item.id) ? ' checked' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          const next = new Set(historyStore.selectedIds);
          if (next.has(item.id)) {
            next.delete(item.id);
          } else {
            next.add(item.id);
            if (!selectionAnchorId) setSelectionAnchorId(item.id);
          }
          historyStore.setSelectedIds(next);
        }}
      />
      <div className="item-header">
        <div className="item-title">
          {editingId === item.id ? (
            <input
              type="text"
              className="item-title-input"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => handleEditKeyDown(item, e)}
              onBlur={() => handleCancelEdit()}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          ) : (
            <>
              <div className="item-title-row">
                {mode === "search" && item.metadata?.turnCount > 0 && (
                  <span className="conversation-badge" title={`Has ${item.metadata.turnCount} ${item.metadata.turnCount === 1 ? 'turn' : 'turns'}`}>
                    <span className="glyphicon glyphicon-comment"/>
                  </span>
                )}
                {runningIds.has(item.id) && <ActiveAgentIcon />}
                {mode === "conversation" && item.unread && (
                  <span className="history-item__unread" title="Finished while you were away" />
                )}
                {item.context?.title || "Untitled"}
              </div>
              {DEV && item.id && (
                <div className="item-uuid">{item.id}</div>
              )}
            </>
          )}
        </div>
        {editingId === item.id && (
          <div
            className="item-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="save-btn visible"
              onClick={(e) => handleSaveEdit(item, e)}
              title="Save"
            >
              <span className="glyphicon glyphicon-ok"/>
            </button>
            <button
              className="cancel-btn visible"
              onClick={(e) => handleCancelEdit(e)}
              title="Cancel"
            >
              <span className="glyphicon glyphicon-remove"/>
            </button>
          </div>
        )}
        {hoverMenu.id === item.id && hoverMenu.type === 'item' && editingId !== item.id && (
          <div
            className={`item-hover-menu${hoverMenu.expanded ? ' expanded' : ''}`}
            onMouseEnter={() => setHoverMenu(m => ({ ...m, expanded: true }))}
            onMouseLeave={() => setHoverMenu(m => ({ ...m, expanded: false }))}
            onClick={e => e.stopPropagation()}
          >
            {hoverMenu.expanded && <>
              <button
                className="hover-menu-btn"
                onClick={(e) => handleDeleteItem(item, e)}
                title={`Delete ${mode}`}
                disabled={regeneratingIds.has(item.id)}
              >
                <span className="glyphicon glyphicon-trash"/>
              </button>
              {mode === "conversation" && (
                <button
                  className="hover-menu-btn"
                  onClick={(e) => handleArchiveItem(item, e)}
                  title="Archive conversation"
                  disabled={regeneratingIds.has(item.id)}
                >
                  <span className="glyphicon glyphicon-inbox"/>
                </button>
              )}
              <button
                className="hover-menu-btn"
                onClick={(e) => handleDuplicateItem(item, e)}
                title={`Duplicate ${mode}`}
                disabled={regeneratingIds.has(item.id)}
              >
                <span className="glyphicon glyphicon-duplicate"/>
              </button>
              <button
                className={`hover-menu-btn${regeneratingIds.has(item.id) ? ' spinning' : ''}`}
                onClick={(e) => handleRegenerateTitle(item, e)}
                title={
                  mode === "conversation" && item.metadata?.turnCount > 0
                    ? "Auto-generate title"
                    : "Use generated title"
                }
                disabled={regeneratingIds.has(item.id)}
              >
                <span className="glyphicon glyphicon-refresh"/>
              </button>
            </>}
            <button
              className="hover-menu-btn pencil-btn"
              onClick={(e) => handleEditItem(item, e)}
              title={`Rename ${mode}`}
              disabled={regeneratingIds.has(item.id)}
            >
              <span className="glyphicon glyphicon-pencil"/>
            </button>
          </div>
        )}
      </div>

      {loadingItem === item.id && (
        <div className="item-loading-overlay">
          <Spinner animation="border" size="sm" />
        </div>
      )}
    </div>
  );

  const renderProject = (project) => {
    const itemsInProject = items.filter(i => i.context?.folderId === project.id);
    if (filterLower && itemsInProject.length === 0) return null;
    const isExpanded = filterLower ? true : isProjectExpanded(project.id);
    const bgColor = project.context?.color;
    const fgColor = bgColor ? getContrastColor(bgColor) : undefined;
    const isEditing = editingProjectId === project.id;
    const isColorPickerOpen = colorPicker.projectId === project.id;

    return (
      <div
        key={project.id}
        className={`project-row${dragState.dragOverProjectId === project.id ? ' drag-over' : ''}`}
        style={bgColor ? { backgroundColor: bgColor, color: fgColor, '--project-bg': bgColor } : undefined}
        onDragOver={(e) => {
          if (dragState.draggingItemProjectId === project.id) return; // own project — not a valid target
          e.preventDefault();
          setDragState(s => ({ ...s, dragOverProjectId: project.id }));
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragState(s => ({ ...s, dragOverProjectId: null }));
          }
        }}
        onDrop={(e) => {
          if (dragState.draggingItemProjectId === project.id) return; // own project — ignore
          e.preventDefault(); e.stopPropagation();
          handleDropOnProject(dragState.draggingItemId, project.id);
          setDragState({ draggingItemId: null, draggingItemProjectId: null, dragOverProjectId: null });
        }}
        onMouseMove={(e) => {
          const overItem = !!e.target.closest('.history-item');
          setHoverMenu(m => {
            if (!overItem && (m.id !== project.id || m.type !== 'project')) {
              return { ...m, id: project.id, type: 'project' };
            }
            if (overItem && m.id === project.id && m.type === 'project') {
              return { id: null, type: null, top: 0, right: 0, expanded: false };
            }
            return m;
          });
        }}
        onMouseLeave={() => {
          setHoverMenu(m => m.id === project.id ? { id: null, type: null, top: 0, right: 0, expanded: false } : m);
        }}
      >
        <div
          className="project-header"
          onClick={() => toggleProject(project.id)}
        >
          <span className="project-chevron">{isExpanded ? '▼' : '▶'}</span>
          {isEditing ? (
            <input
              className="project-title-input"
              value={editProjectTitle}
              autoFocus
              onChange={e => setEditProjectTitle(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  if (editProjectTitle.trim()) {
                    historyStore.updateProject(project.id, { context: { ...project.context, title: editProjectTitle.trim() } });
                  }
                  setEditingProjectId(null);
                }
                if (e.key === 'Escape') setEditingProjectId(null);
              }}
              onBlur={() => {
                if (editProjectTitle.trim()) {
                  historyStore.updateProject(project.id, { context: { ...project.context, title: editProjectTitle.trim() } });
                }
                setEditingProjectId(null);
              }}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <span className="project-title">{project.context?.title || 'Unnamed Project'}</span>
          )}
          <span className="project-count">{itemsInProject.length || 'empty'}</span>
          {hoverMenu.id === project.id && hoverMenu.type === 'project' && !isEditing && (
            <div
              className={`item-hover-menu${hoverMenu.expanded ? ' expanded' : ''}`}
              style={bgColor ? { backgroundColor: bgColor, color: fgColor } : undefined}
              onMouseEnter={() => setHoverMenu(m => ({ ...m, expanded: true }))}
              onMouseLeave={() => setHoverMenu(m => ({ ...m, expanded: false }))}
              onClick={e => e.stopPropagation()}
            >
              {hoverMenu.expanded && (
                <button
                  className="hover-menu-btn"
                  onClick={() => {
                    if (itemsInProject.length === 0) {
                      historyStore.deleteProject(project.id);
                    } else {
                      setDeleteProjectDialog({ open: true, project });
                    }
                  }}
                  title="Delete project"
                >
                  <span className="glyphicon glyphicon-trash" />
                </button>
              )}
              {hoverMenu.expanded && (
                <button
                  className="hover-menu-btn color-btn"
                  title="Set project color"
                  onClick={e => {
                    e.stopPropagation();
                    if (isColorPickerOpen) {
                      setColorPicker(p => ({ ...p, projectId: null }));
                    } else {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setColorPicker({ projectId: project.id, top: rect.bottom + 4, left: rect.left });
                    }
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                    <rect x="0" y="0" width="5" height="5" rx="1"/>
                    <rect x="7" y="0" width="5" height="5" rx="1"/>
                    <rect x="0" y="7" width="5" height="5" rx="1"/>
                    <rect x="7" y="7" width="5" height="5" rx="1"/>
                  </svg>
                </button>
              )}
              {isColorPickerOpen && createPortal(
                <div
                  className="color-palette-popup"
                  style={{ position: 'fixed', top: colorPicker.top, left: colorPicker.left, zIndex: 9999 }}
                  onClick={e => e.stopPropagation()}
                >
                  <div className="color-palette-grid">
                    {PRESET_COLORS.map(c => (
                      <button
                        key={c}
                        className={`color-swatch${bgColor === c ? ' selected' : ''}`}
                        style={{ backgroundColor: c }}
                        onClick={() => { setColorPicker(p => ({ ...p, projectId: null })); Analytics.event(Analytics.EVENTS.HISTORY_PROJECT_COLOR, { project_id: project.id }); historyStore.updateProject(project.id, { context: { ...project.context, color: c } }); }}
                        title={c}
                      />
                    ))}
                    <label className="color-swatch color-swatch-rainbow" title="Custom color…">
                      <input
                        type="color"
                        defaultValue={bgColor || '#4a4a6a'}
                        onChange={e => { setColorPicker(p => ({ ...p, projectId: null })); Analytics.event(Analytics.EVENTS.HISTORY_PROJECT_COLOR, { project_id: project.id }); historyStore.updateProject(project.id, { context: { ...project.context, color: e.target.value } }); }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button
                      className={`color-swatch color-swatch-clear${!bgColor ? ' selected' : ''}`}
                      onClick={() => { setColorPicker(p => ({ ...p, projectId: null })); Analytics.event(Analytics.EVENTS.HISTORY_PROJECT_COLOR, { project_id: project.id }); historyStore.updateProject(project.id, { context: { ...project.context, color: null } }); }}
                      title="Clear color"
                    />
                  </div>
                </div>,
                document.body
              )}
              <button
                className="hover-menu-btn pencil-btn"
                onClick={() => { setEditingProjectId(project.id); setEditProjectTitle(project.context?.title || ''); }}
                title="Rename project"
              >
                <span className="glyphicon glyphicon-pencil" />
              </button>
            </div>
          )}
        </div>
        {isExpanded && (
          <div className="project-items">
            {itemsInProject.map(item => renderItem(item, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={`drawer-toggle-button drawer-toggle-button--${mode}`} onClick={toggleDrawer} title={isOpen ? "Close drawer" : "Open history"}>
        <div className="hamburger-icon">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>

      <div
        className={`history-drawer history-drawer--${mode}${isOpen ? ' open' : ' closed'}${className ? ' ' + className : ''}`}
        onClick={(e) => {
          // Clear selection when clicking on non-item areas of the drawer
          if (historyStore.selectedIds.size > 0 && !e.target.closest('.history-item')) {
            historyStore.setSelectedIds(new Set());
            setSelectionAnchorId(null);
          }
        }}
      >
        <div className="drawer-content" style={{ width: drawerWidth }}>
          <div className="drawer-header">
            <h3>{mode === "conversation" ? "Conversations" : "Searches"}</h3>
            <div className="drawer-header-actions">
              {onNewItem && (
                <div
                  className={`new-item-link${!currentItemId ? ' disabled' : ''}`}
                  onClick={currentItemId ? handleNewItem : undefined}
                  title={`Start a new ${mode}`}
                >
                  <span className="glyphicon glyphicon-plus"></span><span>{newItemLabel}</span>
                </div>
              )}
              <div
                className="new-item-link new-project-link"
                onClick={() => { setCreatingProject(true); setNewProjectName(''); }}
                title="Create new project"
              >
                <span className="glyphicon glyphicon-plus"></span><span>Project</span>
              </div>
              {items.length > 0 && (
                <div
                  className="clear-history-link"
                  onClick={handleClearHistory}
                  title={`Clear history`}
                >
                  <span className="glyphicon glyphicon-trash"></span>
                </div>
              )}
              {mode === "conversation" && (
                <div
                  className={`new-item-link archived-toggle-link${archivedTabActive ? ' active' : ''}`}
                  onClick={() => {
                    if (memoryTabActive) {
                      setDrawerTab('conversations');
                      setArchivedView(true);
                    } else {
                      setArchivedView(v => !v);
                    }
                  }}
                  title="Archived conversations"
                >
                  <span className="glyphicon glyphicon-inbox"></span><span>Archived</span>
                </div>
              )}
              {userStore?.isAdmin && (
                <div
                  className={`new-item-link memory-toggle-link${memoryTabActive ? ' active' : ''}`}
                  onClick={() => setDrawerTab(t => t === 'memory' ? 'conversations' : 'memory')}
                  title="Memory profile"
                >
                  <span>🧠</span><span>Memory</span>
                </div>
              )}
            </div>
          </div>

          {memoryTabActive && (
            <MemoryProfileDrawer userStore={userStore} />
          )}

          {!memoryTabActive && archivedTabActive && (
            <div className="item-list archived-list">
              {historyStore.archived.length === 0 && !historyStore.archivedLoading && (
                <div className="empty-state"><p>No archived conversations</p></div>
              )}
              {historyStore.archived.map(item => (
                <div key={item.id} className="history-item archived-item" onClick={() => handleLoadItem(item)}>
                  <div className="item-title">
                    <div className="item-title-row">{item.context?.title || "Untitled"}</div>
                  </div>
                  <button
                    className="hover-menu-btn archived-restore-btn"
                    onClick={(e) => handleRestoreItem(item, e)}
                    title="Restore conversation"
                  >
                    Restore
                  </button>
                </div>
              ))}
              {historyStore.archivedHasMore && (
                <div className="load-more-container" onClick={() => historyStore.loadArchived({ more: true })}
                     title="Load more items">
                  {historyStore.archivedLoading ? <Spinner animation="border" size="sm" /> : '⋯'}
                </div>
              )}
            </div>
          )}

          {!memoryTabActive && !archivedTabActive && (<>
          {/* Text filter */}
          <div className="history-filter">
            <span className="glyphicon glyphicon-search history-filter-icon" />
            <input
              type="text"
              className="history-filter-input"
              placeholder="Filter…"
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
            />
            {filterText && (
              <button className="history-filter-clear" onClick={() => setFilterText('')} title="Clear filter">✕</button>
            )}
          </div>

          {/* Selection count indicator (when items selected) */}
          {selectedCount > 0 && (
            <div className="selection-indicator">
              <span className="selection-count">{selectedCount} selected</span>
              <div className="selection-actions">
                <div className="bulk-move-container">
                  <button className="selection-action-btn" onClick={e => { e.stopPropagation(); setBulkMoveOpen(o => !o); }}>
                    <span className="glyphicon glyphicon-log-in" /> Move…
                  </button>
                  {bulkMoveOpen && (
                    <div className="move-submenu bulk-move-submenu">
                      {historyStore.sortedProjects.map(project => (
                        <div key={project.id} className="move-submenu-item" onClick={() => handleBulkMove(project.id)}>
                          {project.context?.title}
                        </div>
                      ))}
                      {historyStore.sortedProjects.length > 0 && <div className="move-submenu-divider" />}
                      <div className="move-submenu-item move-submenu-none" onClick={() => handleBulkMove(null)}>
                        (none)
                      </div>
                    </div>
                  )}
                </div>
                <button className="selection-action-btn selection-delete-btn" onClick={() => setBulkDeleteConfirm(true)}>
                  <span className="glyphicon glyphicon-trash" />
                </button>
                <button className="selection-action-btn selection-clear-btn" onClick={() => { historyStore.setSelectedIds(new Set()); setSelectionAnchorId(null); }}>
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Offline indicator */}
          {!historyStore.isOnline && (
            <Alert variant="warning" className="history-offline-alert">
              <strong>Offline</strong>
              <p>Cloud history is unavailable offline.</p>
            </Alert>
          )}

          {/* Error display with retry */}
          {historyStore.error && historyStore.isOnline && (
            <Alert variant="danger" className="history-error-alert">
              <strong>Error loading history</strong>
              <p>{historyStore.error}</p>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleRetry}
                className="history-error-retry"
              >
                Retry
              </Button>
            </Alert>
          )}

          {/* Loading spinner (initial load only) */}
          {historyStore.loading && items.length === 0 && (
            <div className="history-loading">
              <Spinner animation="border" size="sm" /> Loading history...
            </div>
          )}

          <div
            className="item-list"
            onDragOver={e => { if (dragState.draggingItemId) e.preventDefault(); }}
            onDrop={e => {
              e.preventDefault();
              if (dragState.draggingItemId) {
                handleDropOnProject(dragState.draggingItemId, null);
                setDragState({ draggingItemId: null, draggingItemProjectId: null, dragOverProjectId: null });
              }
            }}
          >
            {!historyStore.loading && items.length === 0 && !historyStore.error ? (
              <div className="empty-state">
                {filterLower ? (
                  <p>No matches for "{filterText}"</p>
                ) : (
                  <>
                    <p>{emptyMessage}</p>
                    <p className="hint">{emptyHint}</p>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* Inline project creation row */}
                {creatingProject && (
                  <div className="project-row creating">
                    <div className="project-header">
                      <input
                        className="project-title-input"
                        value={newProjectName}
                        autoFocus
                        placeholder="Project name…"
                        onChange={e => setNewProjectName(e.target.value)}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' || e.key === 'Tab') {
                            e.preventDefault();
                            const title = newProjectName.trim() || 'New Project';
                            try {
                              await historyStore.saveProject({ context: { title } });
                              Analytics.event(Analytics.EVENTS.HISTORY_PROJECT_CREATE, { mode });
                            } catch (err) {
                              console.error('Failed to create project:', err);
                              toast.error(`Failed to create project: ${err.message || 'Unknown error'}`);
                            }
                            setCreatingProject(false);
                          }
                          if (e.key === 'Escape') setCreatingProject(false);
                        }}
                        onBlur={async () => {
                          if (newProjectName.trim()) {
                            try {
                              await historyStore.saveProject({ context: { title: newProjectName.trim() } });
                              Analytics.event(Analytics.EVENTS.HISTORY_PROJECT_CREATE, { mode });
                            } catch (err) {
                              console.error('Failed to create project:', err);
                              toast.error(`Failed to create project: ${err.message || 'Unknown error'}`);
                            }
                          }
                          setCreatingProject(false);
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Project rows — hidden while loading to avoid stale structure from previous mode */}
                {!historyStore.loading && historyStore.sortedProjects && historyStore.sortedProjects.map(project =>
                  renderProject(project)
                )}

                {/* Divider between projects and ungrouped items */}
                {!historyStore.loading && historyStore.sortedProjects && historyStore.sortedProjects.length > 0 && ungroupedItems.length > 0 && (
                  <div className="projects-divider" />
                )}


                {ungroupedItems.map((item) => renderItem(item, false))}

                {/* Load more ellipsis */}
                {historyStore.hasMore && (
                  <div
                    className="load-more-container"
                    onClick={handleLoadMore}
                    style={{
                      cursor: historyStore.loading ? 'wait' : 'pointer',
                      textAlign: 'center',
                      padding: '10px',
                      color: 'var(--text-secondary)',
                      fontSize: '20px',
                      userSelect: 'none'
                    }}
                    title="Load more items"
                  >
                    {historyStore.loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      '⋯'
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          </>)}
        </div>
        <div
          className={`drawer-resize-handle${resizing ? ' dragging' : ''}`}
          onMouseDown={handleResizeMouseDown}
          title="Drag to resize"
        />
      </div>

      {/* Delete Project Dialog */}
      <Modal
        show={deleteProjectDialog.open}
        onHide={() => setDeleteProjectDialog({ open: false, project: null })}
        centered
        className="plex-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete "{deleteProjectDialog.project?.context?.title}"</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            const allItems = [...(historyStore.conversations || []), ...(historyStore.searches || [])];
            const count = allItems.filter(i => i.context?.folderId === deleteProjectDialog.project?.id).length;
            return (
              <p>This project contains <strong>{count} item{count !== 1 ? 's' : ''}</strong>. What would you like to do?</p>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="me-auto" onClick={() => setDeleteProjectDialog({ open: false, project: null })}>
            Cancel
          </Button>
          <Button variant="outline-danger" onClick={handleDeleteProjectOnly}>
            Keep items
          </Button>
          <Button variant="danger" onClick={handleDeleteProjectAndItems}>
            Delete all
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal show={bulkDeleteConfirm} onHide={() => setBulkDeleteConfirm(false)} centered className="plex-modal">
        <Modal.Header closeButton>
          <Modal.Title>Delete {selectedCount} items?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{selectedCount} selected {mode === 'conversation' ? 'conversations' : 'searches'}</strong>?</p>
          <p style={{ color: 'var(--danger-color, #f38ba8)', fontWeight: 500 }}>This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBulkDeleteConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleBulkDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>

      {/* Navigation Confirmation Modal */}
      <Modal
        show={navConfirmItem != null}
        onHide={() => setNavConfirmItem(null)}
        centered
        className="plex-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {mode === 'conversation' ? 'Conversation' : 'Search'} in Progress
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>A {mode === 'conversation' ? 'conversation' : 'search'} is currently active. Opening a different item here will end it.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setNavConfirmItem(null)}>
            Cancel
          </Button>
          <Button variant="outline-primary" onClick={() => handleSwitchHere(navConfirmItem)}>
            Switch Here
          </Button>
          <Button variant="primary" autoFocus onClick={() => handleOpenInNewTab(navConfirmItem)}>
            Open in New Tab
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Clear History Confirmation Modal */}
      <Modal
        show={showClearConfirm}
        onHide={handleCancelClear}
        centered
        className="plex-modal clear-history-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="glyphicon glyphicon-warning-sign" style={{ marginRight: '8px', color: 'var(--danger-color)' }}></span>
            Clear All {mode === 'conversation' ? 'Conversations' : 'Searches'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>all {mode === 'conversation' ? 'conversations' : 'searches'}?</strong></p>
          <p style={{ color: 'var(--danger-color)', fontWeight: '500' }}>This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelClear}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmClear}>
            Delete All {mode === 'conversation' ? 'Conversations' : 'Searches'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default HistoryDrawer;
